import { Injectable, Inject } from '@angular/core';
import { DatePipe } from '@angular/common';
import { GenericService } from './generic.service';
import { PruHttpService } from './pru-http.service';
import { WindowRefService } from './window-ref.service';
import { SessionStorageService } from './web-storage/session-storage.service';
import { DOCUMENT } from '@angular/common';
import { Observable, ReplaySubject } from 'rxjs';
import { retry, map } from 'rxjs/operators';

@Injectable()
export class JsInterstitialService {
  static readonly NEW_TERM_LOW_VAL_INTERSTITIAL_ID: string = '2';
  static readonly PREV_TERM_LOW_VAL_INTERSTITIAL_ID: string = '4';
  static readonly LINK_INTERSTITIAL_ID: string = '7';
  static readonly CONSENT_INTERSTITIAL_ID: string = '8';

  public interstitialId: number;
  public interstitialContentUrl: string;
  public interstitialName: string;

  public isPlanSpecificInterstitial: boolean;
  public planName: string;
  public planTerminatedDate: string; 
  public consentInterstitialVersion: number;
  public consentText: string;
  
  public linkInterstitial: ReplaySubject<any> = new ReplaySubject();
  public linkInterstitial$ = this.linkInterstitial.asObservable();

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private pruHttpService: PruHttpService,
    private _genericService: GenericService,
    private _sessionStorageService: SessionStorageService,
    private _window: WindowRefService,
    private datePipe: DatePipe
  ) {}
  
  // GET call to Interstitial API to determine which interstitial to display 
  checkInterstitialViewed$(sum, permission, goals): Observable<boolean> {
    const currentDDO = this._sessionStorageService.getItem('currentDdo', true);

    const interstitialReqParams = {
      ssoid: currentDDO.user.profile.profileInfo.pruId,
      userSourceCode: 'RET',
      applicationType: 'myaccounts',
      coUserId: currentDDO.user.profile.profileInfo.coUserId,
      balance: sum,
      isPermissioned: permission,
      hasGoals: goals
    };

    return this._genericService
      .getServiceData(
        '/api/co/secure/interstitial/v1/userinterstitial',
        'js-interstitial',
        interstitialReqParams
      )
      .do(jsinterstitial => {
        this.interstitialId = jsinterstitial.interstitialUserDto && jsinterstitial.interstitialUserDto.interstitialid ? jsinterstitial.interstitialUserDto.interstitialid : null;
        this.interstitialContentUrl =  jsinterstitial.interstitialUserDto && jsinterstitial.interstitialUserDto.contentTemplateURL ? jsinterstitial.interstitialUserDto.contentTemplateURL : "";
        this.interstitialName = jsinterstitial.interstitialUserDto && jsinterstitial.interstitialUserDto.interstitialName ? jsinterstitial.interstitialUserDto.interstitialName : "";
        this.isPlanSpecificInterstitial = jsinterstitial.interstitialUserDto && jsinterstitial.interstitialUserDto.planSpecific ? jsinterstitial.interstitialUserDto.planSpecific : "";
        this.planName = jsinterstitial.interstitialUserDto && jsinterstitial.interstitialUserDto.planName ? jsinterstitial.interstitialUserDto.planName : "";
        this.planTerminatedDate = jsinterstitial.interstitialUserDto && jsinterstitial.interstitialUserDto.planTerminatedDate ? jsinterstitial.interstitialUserDto.planTerminatedDate : "";

        if (!jsinterstitial.interstitialUserStatus || !this.isLinkInterstitial()) {
          this.linkInterstitial.next(false);
        }
      })
      .map(
        jsinterstitial => {
          return jsinterstitial.interstitialUserStatus;
        }
      );
  }

    // Determine which Interstitial Content to get (CONSENT, LINK, or Job Switchers). CONSENT requires additional GET call
    determineInterstitialPath(): Observable<any> {
      if (this.isConsentInterstitial()) {
        const consentUrl = '/api/co/secure/consent-management/v1/consents/parties/clientconsents?consentCategory=TCPA&consentName=PRUTCPA';
        
        return this.getConsentApi(consentUrl)
        .flatMap(consent =>{
          // Logic to determine if user qualifies to see CONSENT interstitial before making WCM call
          let noConsent = consent.some(obj =>{ return obj.consentStatus !== 'PENDING'});
  
          if (!consent || !consent.length || noConsent) {
            return null;
          } else {
            consent.forEach(consentObj =>{
              if (consentObj.consentName === 'PRUTCPA') {
                this.consentInterstitialVersion = consentObj.version;
                this.consentText = consentObj.content;
              }
            })
    
            return this.getInterstitialHTMLContent$();
          }
  
        })
        .catch(error =>{
          return error;
        })
  
      } else {
        return this.getInterstitialHTMLContent$();
      }
    }

  // GET WCM Content for CONSENT, LINK, and Plan Terminated which are returned in HTML format
  getInterstitialHTMLContent$(): Observable<any> {
    return this.pruHttpService.getText(this.interstitialContentUrl, 'js-interstitial')
    .pipe(
      map(wcmHTMLContent =>{
        if (this.isPlanSpecific()) {
          wcmHTMLContent = wcmHTMLContent.replace('REPLACE_TOKEN', this.interstitialName);
        } else if (this.isPlanTerminated()) {
          const formattedDate = this.datePipe.transform(this.planTerminatedDate, 'M/d/yyyy', 'UTC-05');
          wcmHTMLContent = wcmHTMLContent.replace('REPLACE_TOKEN_PLAN_NAME', this.planName).replace('REPLACE_TOKEN_TERM_DATE', formattedDate);
        }

        return wcmHTMLContent;
      })
    )
  }

  // GET call to CONSENT API
  getConsentApi(consentUrl): Observable<any> {
    return this._genericService.getServiceData(consentUrl, 'js-interstitial');
  }

  // GET request for PROFILE API 
  getUserProfile$(profileUrl): Observable<any> {
    return this._genericService.getServiceData(profileUrl, 'js-interstitial');
  }

  // Dispatch Analytics events which will be listened to by Analytics Tags
  doInterstitialAnalytics(evtType): void {
    const analyticsData = {
      evtData: {
        evtDesc: this.planName ? this.interstitialName + " - " + this.planName : this.interstitialName,
        evtType: evtType
      }
    };

    if (evtType === 'Display') {
      (this._window.nativeWindow as any).jsInterstitialDisplayVar = analyticsData;
    }

    const analyticsEvt = this.createNewEvent('maHomeModal', analyticsData);

    this.document.dispatchEvent(analyticsEvt);
  }

  // Method to create event for Analytics (condition for IE browsers)
  private createNewEvent(eventName, analyticsData) {
    let event;

    if (typeof(Event) === 'function') {
        event = new Event(eventName);
        event['detail'] = JSON.stringify(analyticsData);
    } else {
        event = document.createEvent('Event');
        event.initEvent(eventName, true, true, { detail: JSON.stringify(analyticsData) });
    }

    return event;
  }

  // PUT request to update Interstitial once it has been viewed
  updateInterstitial(doNotDisplay, increaseViewCount): void {
    const currentDDO = this._sessionStorageService.getItem('currentDdo', true);

    const interstitialReqParams = {
      ssoid: currentDDO.user.profile.profileInfo.pruId,
      interstitialId: this.interstitialId,
      userSourceCode: 'RET',
      applicationType: 'myaccounts',
      coUserId: currentDDO.user.profile.profileInfo.coUserId,
      doNotDisplay: doNotDisplay,
      remindMe: 'F',
      increaseViewCount: increaseViewCount
    };

    this.pruHttpService
      .put(
        '/api/co/secure/interstitial/v1/userinterstitial',
        'JsInterstitialService',
        interstitialReqParams
      )
      .retry(1)
      .subscribe()
  }

  // POST request to update interstitial status as CAPTURE or DECLINE
  updateConsentApi(consentUrl,personalInfo, phoneObj?): Observable<any> {
    const currentDDO = this._sessionStorageService.getItem('currentDdo', true);
    const coUserIdHeader = {"X-Pru-Imp-CO-USER-ID": currentDDO.user.profile.profileInfo.coUserId}
    let consentReqParams = {
      "firstName": personalInfo.firstName,
      "lastName": personalInfo.lastName,
      "consentSource": "MYACCOUNTS",
      "channelDetail": "www.prudential.com",
      "overridePermission": "N"
    }

    if (phoneObj) {
      consentReqParams = Object.assign(consentReqParams, phoneObj)
    }

    return this.pruHttpService
      .post(
        consentUrl,
        'JsInterstitialService',
        consentReqParams,
        coUserIdHeader
      )
      .retry(1)
  }

  // Check if Interstitial is LINK type
  isLinkInterstitial() {
    return this.interstitialId.toString().charAt(0) === JsInterstitialService.LINK_INTERSTITIAL_ID;
  }

  // Check if Interstitial is CONSENT type
  isConsentInterstitial() {
    return this.interstitialId.toString().charAt(0) === JsInterstitialService.CONSENT_INTERSTITIAL_ID;
  }

  // Check if Interstitial is Plan Terminated type
  isPlanTerminated() {
    return this.isPlanSpecificInterstitial && this.planName && this.planTerminatedDate;
  }

  isPlanSpecific() {
    return this.isPlanSpecificInterstitial && !this.planTerminatedDate;
  }

  //Check if Interstitial is Low Value Type (could be previously termed or newly termed)
  isLowValueInterstitial() {
    return this.interstitialId.toString().charAt(0) === JsInterstitialService.NEW_TERM_LOW_VAL_INTERSTITIAL_ID || this.interstitialId.toString().charAt(0) === JsInterstitialService.PREV_TERM_LOW_VAL_INTERSTITIAL_ID;
  }
}
